import { ref, watch, onUnmounted } from "vue"
import { supabase } from "@/lib/api"
import { useRouter } from "vue-router"
import type { AuthChangeEvent, Session, UserAttributes } from "@supabase/supabase-js"
import { debug, error as logError } from "@/utils/debug"

export const user = ref<Awaited<ReturnType<typeof supabase.auth.getUser>>["data"]["user"] | null>(
  null,
)
const loading = ref(true)
const ready = ref(false)

let authSubscription:
  | ReturnType<typeof supabase.auth.onAuthStateChange>["data"]["subscription"]
  | null = null

async function initAuth() {
  if (ready.value) return
  loading.value = true

  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession()

    if (sessionError) {
      logError("[Auth] Error getting session:", sessionError)
      user.value = null
    } else {
      user.value = sessionData.session?.user ?? null
    }

    if (!authSubscription) {
      const { data } = supabase.auth.onAuthStateChange(
        (event: AuthChangeEvent, session: Session | null) => {
          debug(`[Auth] State change: ${event}`)
          user.value = session?.user ?? null
        },
      )
      authSubscription = data.subscription
    }

    ready.value = true
    loading.value = false
  } catch (error) {
    logError("[Auth] Fatal error during initialization:", error)
    user.value = null
    ready.value = true
    loading.value = false
  }
}

function ensureReady() {
  if (ready.value) return Promise.resolve()
  return new Promise<void>((resolve) => {
    const stop = watch(ready, (v) => {
      if (v) {
        stop()
        resolve()
      }
    })
  })
}

function cleanupAuth() {
  if (authSubscription) {
    debug("[Auth] Cleaning up subscription")
    authSubscription.unsubscribe()
    authSubscription = null
  }
}

export function useAuth() {
  const router = useRouter()
  void initAuth()

  onUnmounted(() => {
    cleanupAuth()
  })

  async function signInWithEmail(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    // Immediately update user state on successful login
    if (data.user && !error) {
      user.value = data.user
    }

    return { data, error }
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut({ scope: "global" })
    if (error) {
      logError(error)
      return
    }
    // Immediately clear user state
    user.value = null
    // Redirect to login
    await router.replace({ name: "Auth" })
  }

  async function changePassword(password: string) {
    const { data, error } = await supabase.auth.updateUser({ password })
    if (error) logError(error)
    return { data, error }
  }

  async function updateUser(attributes: UserAttributes, options?: { emailRedirectTo?: string }) {
    const envUrl = getURL()
    const { data, error } = await supabase.auth.updateUser(
      { ...attributes },
      {
        emailRedirectTo: options?.emailRedirectTo || `${envUrl}confirm-email-change`,
      },
    )
    if (error) logError(error)
    return { data, error }
  }

  async function resetPassword(email: string) {
    const envUrl = getURL()
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${envUrl}/reset-password`,
    })
    if (error) logError(error)
    return { data, error }
  }

  return {
    user,
    loading,
    signInWithEmail,
    signOut,
    ensureReady,
    changePassword,
    updateUser,
    resetPassword,
  }
}

const getURL = (): string => {
  let url = import.meta.env?.VITE_APP_URL
  url = url.startsWith("http") ? url : `https://${url}`
  url = url.endsWith("/") ? url : `${url}/`
  return url
}
